import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import {
  Box,
  List,
  ListItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import {
  useCancelRetentionQueueItemMutation,
  useGetUpcomingRetentionActionsQuery,
} from '../../../../graphql/generated';
import { ExpandableCard } from '../../molecules/ExpandableCard';
import { LensButton } from '../../molecules/LensButton';
import { RuleFieldsList } from '../../molecules/RuleFieldsList';
import {
  buildRuleFields,
  calculateTimeLeft,
  useContentPadding,
  useRuleConditionLookups,
} from '../../utils';

export const UpcomingActions = () => {
  const [expandedCases, setExpandedCases] = useState<Record<string, boolean>>(
    {},
  );
  const history = useHistory();

  const queryClient = useQueryClient();
  const { data, isLoading, refetch } = useGetUpcomingRetentionActionsQuery();
  const { mutate: cancelRetentionQueueItem } =
    useCancelRetentionQueueItemMutation({
      onSuccess: () => {
        refetch();
        queryClient.invalidateQueries(['GetUpcomingRetentionActionsCount']);
      },
      onError: (error) =>
        console.error('Failed to remove item from queue:', error),
    });
  const { tokens } = useTheme<LensTheme>();
  const contentPadding = useContentPadding();

  const upcomingActions = data?.getUpcomingRetentionActions ?? [];

  const { relationshipTypes, caseCategories } = useRuleConditionLookups(
    upcomingActions.map((action) => action.RetentionRule?.conditions),
  );

  const ruleFields = buildRuleFields(relationshipTypes, caseCategories);

  const toggleCase = (caseId: string) => {
    setExpandedCases((prev) => ({
      ...prev,
      [caseId]: !prev[caseId],
    }));
  };

  const handleRemoveFromQueue = (queueItemId: string) => {
    cancelRetentionQueueItem({ queueItemId });
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: tokens.core.spacing[3].value,
        }}
      >
        Loading...
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.core.spacing[3].value,
      }}
    >
      <Stack
        direction="column"
        sx={{
          gap: tokens.core.spacing['0_5'].value,
          padding: tokens.core.spacing[0].value,
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Typography
          variant="h3"
          sx={{
            fontWeight: tokens.semantic.fontWeight.emphasis.value,
          }}
        >
          Upcoming Actions
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: tokens.semantic.color.type.muted.value,
          }}
        >
          Showing cases with scheduled data retention actions in the next 7 days
        </Typography>
      </Stack>
      <List
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: tokens.core.spacing[2].value,
          margin: 0,
          padding: 0,
          listStyle: 'none',
        }}
      >
        {upcomingActions.map((action) => {
          const isExpanded = expandedCases[action.id];
          const timeLeftLabel = calculateTimeLeft(action.dueAt);
          const caseTitle =
            action.Case?.name || action.Case?.readableId || action.caseId;
          const country = action.RetentionRule?.country;
          const countryRule = action.RetentionRule?.countryRule;
          const subtitle =
            !countryRule && !country
              ? 'Fallback Rule'
              : `${countryRule} - ${country}`;

          const fieldData = ruleFields.map((field) => ({
            label: field.label,
            value: field.getValue(action),
          }));

          return (
            <ExpandableCard
              variant="actions"
              key={action.id}
              title={caseTitle}
              subtitle={subtitle}
              toggleKey={action.id}
              items={[action]}
              isExpanded={isExpanded}
              onToggle={toggleCase}
              chipLabel={timeLeftLabel}
              renderExpandedContent={() => (
                <ListItem
                  sx={{
                    padding: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: tokens.core.spacing[2].value,
                    width: '100%',
                    boxShadow: 'none',
                  }}
                >
                  <ListItem
                    sx={{
                      padding: contentPadding,
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: tokens.core.spacing['1_5'].value,
                      width: '100%',
                      boxShadow: 'none',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: tokens.semantic.fontWeight.emphasis.value,
                      }}
                    >
                      Rule configuration
                    </Typography>
                  </ListItem>
                  <ListItem
                    sx={{
                      padding: contentPadding,
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: tokens.core.spacing[0].value,
                      width: '100%',
                      boxShadow: 'none',
                    }}
                  >
                    <RuleFieldsList fields={fieldData} variant="emphasis" />
                    <RuleFieldsList fields={fieldData} variant="body" />
                  </ListItem>
                  <ListItem
                    sx={{
                      padding: 0,
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: tokens.core.spacing[2].value,
                      width: '100%',
                      boxShadow: 'none',
                    }}
                  >
                    <LensButton
                      variant="secondary"
                      buttonSize="small"
                      onClick={() => handleRemoveFromQueue(action.id)}
                    >
                      Remove from queue
                    </LensButton>
                    <LensButton
                      variant="primary"
                      buttonSize="small"
                      onClick={() => history.push(`/case?id=${action.caseId}`)}
                    >
                      Open case
                    </LensButton>
                  </ListItem>
                </ListItem>
              )}
            />
          );
        })}
      </List>
    </Box>
  );
};
